import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button/Button';
import Backdrop from 'components/Modal/Backdrop';
import { BUTTON_VARIANT } from 'constants/interfaces';

import type { IDeleteRestrictedModal } from './interfaces/IDeleteRestrictedModal';

import styles from './scss/DeleteRestrictedModal.module.scss';

function DeleteRestrictedModal({ isOpen, onClose, districtErrors }: IDeleteRestrictedModal) {
  const { t } = useTranslation();

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  return isOpen ? createPortal(
    <Backdrop>
      <div
        className={styles.modalOverlay}
      >
        <div
          className={styles.modalContent}
        >
          <h2 id="modal-title" className={styles.modalTitle}>
            {t('common.deletionRestricted')}
          </h2>
          <div className={styles.modalBody}>
            <p>{t('common.deletionRestrictedDescription', { name: districtErrors?.columnName })}</p>
            <div className={styles.modalBodyList}>
              <ul aria-label="Cards preventing deletion" className={styles.modalBodyListItems}>
                {districtErrors?.units?.balanceSheetAssets
                && districtErrors?.units?.balanceSheetAssets?.length > 0
                  ? districtErrors?.units.balanceSheetAssets.map((balanceSheetAsset) => (
                    <li key={[balanceSheetAsset.uniqueIdentifier, balanceSheetAsset.name].join('_')}>
                      {t('common.balanceSheetAssets')}
                      {' '}
                      <strong>{balanceSheetAsset.name}</strong>
                      {' '}
                      {balanceSheetAsset?.linked.length > 0 && balanceSheetAsset?.linked.map((linked) => (
                        <span key={[linked.uniqueIdentifier, linked.name].join('_')}>
                          {t('common.linkedTo')}
                          {' '}
                          {linked.name}
                          {' '}
                          (
                          {linked.uniqueIdentifier}
                          )
                        </span>
                      ))}
                    </li>
                  )) : null}
                {districtErrors?.units?.customers
                  && districtErrors?.units?.customers?.length > 0
                  ? districtErrors?.units.customers.map((customer) => (
                    <li key={[customer.uniqueIdentifier, customer.name].join('_')}>
                      {t('common.customer')}
                      {' '}
                      <strong>{customer.name}</strong>
                      {' '}
                      {customer?.linked.length > 0 && customer?.linked.map((linked) => (
                        <span key={[linked.uniqueIdentifier, linked.name].join('_')}>
                          {t('common.linkedTo')}
                          {' '}
                          {linked.name}
                          {' '}
                          (
                          {linked.uniqueIdentifier}
                          )
                        </span>
                      ))}
                    </li>
                  )) : null}
                {districtErrors?.units?.products
                  && districtErrors?.units?.products?.length > 0
                  ? districtErrors?.units.products.map((product) => (
                    <li key={[product.uniqueIdentifier, product.name].join('_')}>
                      {t('common.product')}
                      {' '}
                      <strong>{product.name}</strong>
                      {' '}
                      {product?.linked.length > 0 && product?.linked.map((linked) => (
                        <span key={[linked.uniqueIdentifier, linked.name].join('_')}>
                          {t('common.linkedTo')}
                          {' '}
                          {linked.name}
                          {' '}
                          (
                          {linked.uniqueIdentifier}
                          )
                        </span>
                      ))}
                    </li>
                  )) : null}
                {districtErrors?.units?.suppliers
                  && districtErrors?.units?.suppliers?.length > 0
                  ? districtErrors?.units.suppliers.map((supplier) => (
                    <li key={[supplier.uniqueIdentifier, supplier.name].join('_')}>
                      {t('common.supplier')}
                      {' '}
                      <strong>{supplier.name}</strong>
                      {' '}

                      {supplier?.linked.length > 0 && supplier?.linked.map((linked) => (
                        <span key={[linked.uniqueIdentifier, linked.name].join('_')}>
                          {t('common.linkedTo')}
                          {' '}
                          {linked.name}
                          {' '}
                          (
                          {linked.uniqueIdentifier}
                          )
                        </span>
                      ))}
                    </li>
                  )) : null}
                { districtErrors?.units?.profitCenters
                && districtErrors?.units?.profitCenters?.length > 0
                  ? districtErrors?.units.profitCenters.map((profitCenter) => (
                    <li key={[profitCenter.uniqueIdentifier, profitCenter.name].join('_')}>
                      {t('common.profitCenter')}
                      {' '}
                      <strong>{profitCenter.name}</strong>
                      {' '}
                      (
                      {profitCenter.uniqueIdentifier}
                      )
                    </li>
                  )) : null}
              </ul>
            </div>
            <p>{t('common.deletionRestrictedDescriptionBottom')}</p>
          </div>

          <div className={styles.modalFooter}>
            <Button
              title={t('common.cancel')}
              variant={BUTTON_VARIANT.cancel}
              onClick={onClose}
            />
          </div>

        </div>
      </div>
    </Backdrop>,
    document.body,
  ) : null;
}

export default DeleteRestrictedModal;
